// /docs/features — capability-level tour.
// Navy/teal/cream; no emoji. Block diagrams only; audience-appropriate
// capability language only.
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const CAPABILITIES = [
    {
        label: "Airframe depth",
        body:
            "Aerodynamic coefficients, control-surface authority, mass and " +
            "inertia, and thrust envelope — plus a repeatable verification flight that " +
            "classifies each airframe as verified, needs review, or failed before " +
            "it is ever used in a run.",
    },
    {
        label: "Environment fidelity",
        body:
            "Wind shear, turbulence, visibility, temperature, pressure, and " +
            "humidity. The same environment drives every backend deterministically " +
            "so that trade studies compare like-for-like.",
    },
    {
        label: "Terrain + RF line-of-sight",
        body:
            "Digital-elevation terrain with obstacle overlays, and an RF " +
            "line-of-sight raycaster that surfaces denial heat-maps for the operator " +
            "before a run is committed.",
    },
    {
        label: "Threat modelling",
        body:
            "Six doctrinal modes — single-waypoint, patrol, swarm, evasive with a " +
            "scripted jink schedule, comms-denied with on-detection fallback, and " +
            "loitering terminal-commit. All threat packs ship as versioned, " +
            "citation-backed presets.",
    },
    {
        label: "Guidance laws",
        body:
            "Pure-pursuit, proportional navigation (N=3/4/5), augmented PN, " +
            "command-to-line-of-sight, and adaptive hybrid — each with its own seeker " +
            "pack (RF or EO/IR) and explicit lock-loss / reacquisition rules.",
    },
    {
        label: "Engagement + lethality",
        body:
            "Warhead type (blast-fragmentation, kinetic, net-capture, directed " +
            "energy), fuzing (proximity, contact, time-after-launch), Pk-vs-miss " +
            "curves, and full salvo doctrine including shoot-look-shoot and " +
            "shoot-shoot-look.",
    },
    {
        label: "Review + provenance",
        body:
            "Every run captures an immutable snapshot of every parameter it used, " +
            "including the source and the citation. A reviewer can approve, request " +
            "changes with notes, or reject — and only approved runs are eligible to " +
            "be promoted to a golden reference.",
    },
];

export default function DocsFeaturesPage() {
    return (
        <>
            <SeoHead path="/docs/features"
                     title="UAVSimNet Docs — What the platform does"
                     description="Capability-level tour across airframes, environment, threats, guidance, engagements, and reviewer sign-off." />
            <div data-testid="docs-features-page">
                <section className="section" style={{ paddingTop: 64 }}>
                    <div className="section__inner">
                        <span className="section__eyebrow">// DOCS · 1 OF 4</span>
                        <h1 className="section__heading">What UAVSimNet does</h1>
                        <p className="section__lede">
                            UAVSimNet is a counter-UAS simulation workbench.
                            Operators assemble a scenario from validated building
                            blocks, run it through a deterministic Monte Carlo
                            engine, and walk away with a defensible engagement
                            record. Seven capability surfaces make that possible.
                        </p>

                        <figure
                            data-testid="docs-features-hero"
                            style={{
                                marginTop: 32,
                                padding: 16,
                                border: "1px solid var(--ux-border-1)",
                                borderRadius: 8,
                                background: "var(--ux-bg-soft, #11181f)",
                            }}
                        >
                            <img
                                src="/docs/screens/workbench_overview_redacted.png"
                                alt="Workbench overview — scenario tree on the left, centre pane with capability tabs, evidence rail on the right. Parameter values are deliberately redacted."
                                style={{ width: "100%", borderRadius: 6, display: "block" }}
                            />
                            <figcaption
                                style={{
                                    marginTop: 10,
                                    color: "var(--ux-text-3)",
                                    fontSize: 12,
                                    fontFamily: "'JetBrains Mono', monospace",
                                }}
                            >
                                Workbench overview — three-column operator UI with
                                the scenario tree, capability tabs, and the
                                evidence rail. Exact parameter values redacted.
                            </figcaption>
                        </figure>

                        <div
                            style={{
                                marginTop: 44,
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(auto-fit, minmax(280px, 1fr))",
                                gap: 18,
                            }}
                        >
                            {CAPABILITIES.map((c) => (
                                <div
                                    key={c.label}
                                    className="cap-card"
                                    data-testid={`docs-feature-${c.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                >
                                    <div
                                        style={{
                                            fontFamily:
                                                "'JetBrains Mono', monospace",
                                            color: "var(--ux-text-1)",
                                            fontSize: 13,
                                            marginBottom: 8,
                                        }}
                                    >
                                        {c.label}
                                    </div>
                                    <p
                                        style={{
                                            color: "var(--ux-text-3)",
                                            fontSize: 13,
                                            lineHeight: 1.55,
                                            margin: 0,
                                        }}
                                    >
                                        {c.body}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div style={{ marginTop: 48, display: "flex", gap: 14, flexWrap: "wrap" }}>
                            <Link to="/docs/how-it-works" className="btn-primary" data-testid="docs-features-next">
                                Next · How it works <ArrowRight size={14} />
                            </Link>
                            <Link to="/docs" className="btn-ghost" data-testid="docs-features-back">
                                Back to docs index
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
}
